import { API_URL } from '@constants';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useState } from 'react';

const ENDPOINT_URL = '/prd-product-images/my-account/reorder';
const ENDPOINT_URL_WITH_VERSION = ENDPOINT_URL;

export interface ReorderImagesPayload {
    product_uuid: string;
    display_order: Record<string, number>;
}

export const useReorderProductImages = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();

    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<number | null>(null);

    const request = async (payload: ReorderImagesPayload) => {
        setLoading(true);
        setErrorMessage(null);
        setSuccess(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL_WITH_VERSION}`, {
                method: 'POST',
                headers: {
                    ...getHeaders(),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...payload,
                    ...getCommonParameters(),
                }),
            });

            if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message || '';
                } catch {
                    error.message = '';
                }

                error.statusCode = response.status;
                throw error;
            }

            const json = await response.json();
            setSuccess(json.message || 'Order updated successfully');
        } catch (err: unknown) {
            const apiErr = err as ApiError;
            setErrorMessage(apiErr.message || 'Failed to reorder images');
            handleCommonErrors(apiErr);
        } finally {
            setLastResponseTime(Date.now());
            setLoading(false);
        }
    };

    return {
        request,
        loading,
        errorMessage,
        success,
        lastResponseTime,
    };
};
